/* ==========================================================================
   du-events.js
   1. Bridges the store's own interactions into analytics (added 2026-08-10)
   2. Announces state changes to screen readers (added 2026-08-10)

   WHY PART 1 EXISTS
   GTM is installed as a Shopify Customer Events custom pixel - a deliberate,
   good choice, because it reaches checkout and thank-you pages and survives
   theme swaps. But it runs inside a sandboxed cross-origin iframe, so it can
   only see the ~13 events Shopify itself publishes. Everything this store
   built by hand lives in the parent document, invisible to the pixel.

   WHY PART 2 EXISTS
   Measured against ridge.com on the same viewport: they carry 15 aria-live
   regions, this store carried 1. A sighted customer taps Start Training and
   sees the drawer open and the count go 0 to 1. A customer using VoiceOver or
   TalkBack heard NOTHING - no confirmation the tap did anything at all.

   It is silent for everyone else. aria-live emits no sound; it is text in a
   visually hidden box that only assistive technology reads, exactly like the
   alt text already on all 57 product images. Screen readers are off by
   default on every phone and desktop.

   DESIGN RULES FOLLOWED HERE
   - No visible DOM is created, moved, styled or removed. The only element
     added is a 1px clipped live region, so nothing here can change the look.
   - Listeners are delegated on document, so section re-renders keep working.
   - Capture phase is used because the <details> toggle event does NOT bubble.
   - The fetch wrapper always calls through and never blocks or rewrites a
     request. It only reads a clone of the response.
   - Everything is wrapped in try/catch, so if an API changes this file goes
     quiet instead of throwing errors onto the page.
   ========================================================================== */

(function () {
  'use strict';

  function publish(name, payload) {
    try {
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (e) {}
  }

  function text(el, max) {
    if (!el) return '';
    return (el.innerText || el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, max || 100);
  }

  function money(cents) {
    try { return '$' + (cents / 100).toFixed(2); } catch (e) { return ''; }
  }

  function plural(n) { return n === 1 ? '' : 's'; }

  /* ------------------------------------------------- screen-reader output */

  var live = null;

  function liveRegion() {
    if (live && document.body && document.body.contains(live)) return live;
    live = document.createElement('div');
    live.id = 'du-live-region';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    /* The standard visually-hidden recipe. Deliberately NOT display:none and
       NOT visibility:hidden - both remove the node from the accessibility
       tree, which would defeat the entire purpose. */
    live.style.cssText = 'position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0';
    document.body.appendChild(live);
    return live;
  }

  var lastSaid = '';
  function announce(msg) {
    try {
      if (!msg || msg === lastSaid) return;
      lastSaid = msg;
      var r = liveRegion();
      /* Clearing first forces a re-announcement when the same text repeats. */
      r.textContent = '';
      setTimeout(function () { r.textContent = msg; }, 60);
    } catch (e) {}
  }

  /* ------------------------------------------ 1. finish / variant selected */

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || t.type !== 'radio') return;
    if (!/^colou?r$/i.test(t.name || '')) return;
    publish('du_finish_selected', { finish: (t.value || '').slice(0, 60), page: location.pathname });
    announce('Finish selected: ' + (t.value || ''));
  }, true);

  /* --------------------------------------------------- 2. persona selected */

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var tab = t.closest('.psc-pills [role="tab"], .psc-pills button');
    if (!tab) return;
    publish('du_persona_selected', { persona: text(tab, 40), page: location.pathname });
    announce('Showing: ' + text(tab, 40));
  }, true);

  /* -------------------------------------------------------- 3. FAQ opened */

  document.addEventListener('toggle', function (e) {
    var d = e.target;
    if (!d || d.tagName !== 'DETAILS' || !d.open) return;
    if (!d.matches || !d.matches('.pfq-i, .pfq2-i')) return;
    publish('du_faq_opened', { question: text(d.querySelector('summary'), 120), page: location.pathname });
  }, true);

  /* ------------------------------- 4. cart changes -> screen-reader output */

  /* Wrapping fetch is the only reliable way to catch this: the theme updates
     the cart over AJAX and re-renders the drawer, so there is no single DOM
     node to watch. Read-only - it awaits the real response, reads a CLONE,
     and returns the original untouched. */
  try {
    var origFetch = window.fetch;
    if (typeof origFetch === 'function') {
      window.fetch = function (input, init) {
        var url = '';
        try { url = (typeof input === 'string') ? input : (input && input.url) || ''; } catch (e) {}
        var p = origFetch.apply(this, arguments);
        if (/\/cart\/(add|change|update)/.test(url)) {
          p.then(function (res) {
            try {
              res.clone().json().then(function (data) {
                if (!data) return;
                if (/\/cart\/add/.test(url)) {
                  announce('Added to cart: ' + (data.product_title || data.title || 'item') + '.');
                  origFetch('/cart.js').then(function (c) { return c.json(); }).then(function (cart) {
                    if (cart) announce('Cart: ' + cart.item_count + ' item' + plural(cart.item_count) + ', total ' + money(cart.total_price) + '.');
                  }).catch(function () {});
                } else if (typeof data.item_count !== 'undefined') {
                  announce('Cart updated: ' + data.item_count + ' item' + plural(data.item_count) + ', total ' + money(data.total_price) + '.');
                }
              }).catch(function () {});
            } catch (e) {}
          }).catch(function () {});
        }
        return p;
      };
    }
  } catch (e) {}
})();
